/**
 * Lesson 8: Sandbox Mode
 *
 * Free exploration with all tools enabled.
 * Multiple scenarios to practice with.
 */

import type { Lesson, SimulationScenario } from '../manager'
import type { Market, OrderBook, Order } from '../../engine/types'

// Helper to create an order
function createOrder(
  id: string,
  side: 'buy' | 'sell',
  price: number,
  quantity: number,
  timestamp: number
): Order {
  return {
    id,
    side,
    type: 'limit',
    price,
    quantity,
    timestamp,
    status: 'open',
    filledQuantity: 0,
  }
}

// =============================================================================
// Sandbox Scenario 1: Balanced Market
// =============================================================================

const balancedOrderBook: OrderBook = {
  bids: [
    createOrder('bal-bid-1', 'buy', 0.49, 100, Date.now() - 5000),
    createOrder('bal-bid-2', 'buy', 0.48, 150, Date.now() - 4000),
    createOrder('bal-bid-3', 'buy', 0.46, 200, Date.now() - 3000),
    createOrder('bal-bid-4', 'buy', 0.44, 120, Date.now() - 2000),
    createOrder('bal-bid-5', 'buy', 0.42, 80, Date.now() - 1000),
  ],
  asks: [
    createOrder('bal-ask-1', 'sell', 0.51, 100, Date.now() - 5000),
    createOrder('bal-ask-2', 'sell', 0.52, 150, Date.now() - 4000),
    createOrder('bal-ask-3', 'sell', 0.54, 200, Date.now() - 3000),
    createOrder('bal-ask-4', 'sell', 0.56, 120, Date.now() - 2000),
    createOrder('bal-ask-5', 'sell', 0.58, 80, Date.now() - 1000),
  ],
  trades: [],
  lastPrice: 0.50,
}

const balancedMarket: Market = {
  id: 'sandbox-balanced',
  question: 'Will it rain in New York City tomorrow?',
  description: 'A balanced market with a 50/50 probability',
  outcomes: ['Yes', 'No'] as const,
  orderBook: balancedOrderBook,
  resolved: false,
  resolution: null,
}

export const balancedScenario: SimulationScenario = {
  id: 'sandbox-balanced',
  name: 'Balanced Market',
  description: 'A 50/50 market with good liquidity on both sides',
  market: balancedMarket,
  initialOrderBook: balancedOrderBook,
  userStartingBalance: 1000,
}

// =============================================================================
// Sandbox Scenario 2: Bullish Market (High Probability)
// =============================================================================

const bullishOrderBook: OrderBook = {
  bids: [
    createOrder('bull-bid-1', 'buy', 0.78, 80, Date.now() - 5000),
    createOrder('bull-bid-2', 'buy', 0.76, 120, Date.now() - 4000),
    createOrder('bull-bid-3', 'buy', 0.74, 180, Date.now() - 3000),
    createOrder('bull-bid-4', 'buy', 0.72, 100, Date.now() - 2000),
    createOrder('bull-bid-5', 'buy', 0.70, 60, Date.now() - 1000),
  ],
  asks: [
    createOrder('bull-ask-1', 'sell', 0.82, 40, Date.now() - 5000),
    createOrder('bull-ask-2', 'sell', 0.84, 60, Date.now() - 4000),
    createOrder('bull-ask-3', 'sell', 0.86, 80, Date.now() - 3000),
    createOrder('bull-ask-4', 'sell', 0.88, 50, Date.now() - 2000),
    createOrder('bull-ask-5', 'sell', 0.90, 30, Date.now() - 1000),
  ],
  trades: [],
  lastPrice: 0.80,
}

const bullishMarket: Market = {
  id: 'sandbox-bullish',
  question: 'Will the incumbent win the election?',
  description: 'A market where the outcome is considered likely',
  outcomes: ['Yes', 'No'] as const,
  orderBook: bullishOrderBook,
  resolved: false,
  resolution: null,
}

export const bullishScenario: SimulationScenario = {
  id: 'sandbox-bullish',
  name: 'Bullish Market',
  description: 'High probability (~80%) - the favorite is expected to win',
  market: bullishMarket,
  initialOrderBook: bullishOrderBook,
  userStartingBalance: 1000,
}

// =============================================================================
// Sandbox Scenario 3: Bearish Market (Low Probability)
// =============================================================================

const bearishOrderBook: OrderBook = {
  bids: [
    createOrder('bear-bid-1', 'buy', 0.18, 40, Date.now() - 5000),
    createOrder('bear-bid-2', 'buy', 0.16, 60, Date.now() - 4000),
    createOrder('bear-bid-3', 'buy', 0.14, 80, Date.now() - 3000),
    createOrder('bear-bid-4', 'buy', 0.12, 50, Date.now() - 2000),
    createOrder('bear-bid-5', 'buy', 0.10, 30, Date.now() - 1000),
  ],
  asks: [
    createOrder('bear-ask-1', 'sell', 0.22, 80, Date.now() - 5000),
    createOrder('bear-ask-2', 'sell', 0.24, 120, Date.now() - 4000),
    createOrder('bear-ask-3', 'sell', 0.26, 180, Date.now() - 3000),
    createOrder('bear-ask-4', 'sell', 0.28, 100, Date.now() - 2000),
    createOrder('bear-ask-5', 'sell', 0.30, 60, Date.now() - 1000),
  ],
  trades: [],
  lastPrice: 0.20,
}

const bearishMarket: Market = {
  id: 'sandbox-bearish',
  question: 'Will the underdog team win the championship?',
  description: 'A market where the outcome is considered unlikely',
  outcomes: ['Yes', 'No'] as const,
  orderBook: bearishOrderBook,
  resolved: false,
  resolution: null,
}

export const bearishScenario: SimulationScenario = {
  id: 'sandbox-bearish',
  name: 'Bearish Market',
  description: 'Low probability (~20%) - a long shot',
  market: bearishMarket,
  initialOrderBook: bearishOrderBook,
  userStartingBalance: 1000,
}

// =============================================================================
// Sandbox Scenario 4: Volatile Market (Wide Spread)
// =============================================================================

const volatileOrderBook: OrderBook = {
  bids: [
    createOrder('vol-bid-1', 'buy', 0.40, 30, Date.now() - 5000),
    createOrder('vol-bid-2', 'buy', 0.35, 50, Date.now() - 4000),
    createOrder('vol-bid-3', 'buy', 0.30, 40, Date.now() - 3000),
  ],
  asks: [
    createOrder('vol-ask-1', 'sell', 0.60, 30, Date.now() - 5000),
    createOrder('vol-ask-2', 'sell', 0.65, 50, Date.now() - 4000),
    createOrder('vol-ask-3', 'sell', 0.70, 40, Date.now() - 3000),
  ],
  trades: [],
  lastPrice: 0.50,
}

const volatileMarket: Market = {
  id: 'sandbox-volatile',
  question: 'Will the new technology be commercially successful?',
  description: 'An uncertain market with wide spreads',
  outcomes: ['Yes', 'No'] as const,
  orderBook: volatileOrderBook,
  resolved: false,
  resolution: null,
}

export const volatileScenario: SimulationScenario = {
  id: 'sandbox-volatile',
  name: 'Volatile Market',
  description: 'High uncertainty - wide spread, thin liquidity',
  market: volatileMarket,
  initialOrderBook: volatileOrderBook,
  userStartingBalance: 1000,
}

// =============================================================================
// Lesson 8: Sandbox Mode
// =============================================================================

export const lesson8: Lesson = {
  id: 'lesson-8',
  title: 'Sandbox Mode',
  description: 'Free exploration with all trading tools',
  order: 8,
  prerequisites: ['lesson-6'],
  completionCriteria: { type: 'all-content' },
  objectives: [
    'Practice everything you\'ve learned',
    'Experiment with different trading strategies',
    'Build confidence before trading with real money',
  ],
  content: [
    // Page 1: Welcome to Sandbox
    {
      type: 'text',
      data: {
        markdown: `# 🎉 Congratulations!

You've completed all the lessons! Now it's time for **free exploration**.

## What is Sandbox Mode?

Sandbox mode gives you:
- **$1,000** in virtual money
- **All trading tools** enabled (market orders, limit orders, cancel)
- **Multiple market scenarios** to practice with
- **No consequences** - reset anytime and try again!

## What to Practice

1. **Place market orders** to trade instantly
2. **Place limit orders** to set your own prices
3. **Cancel orders** if you change your mind
4. **Watch the order book** change as you trade
5. **Track your P&L** in the position summary

> **Remember**: This is simulated money. Experiment freely and learn from your trades!`,
      },
    },

    // Page 2: Available Scenarios
    {
      type: 'text',
      data: {
        markdown: `## Available Market Scenarios

Choose different scenarios to practice various situations:

### 1. Balanced Market 🟰
- **Probability**: ~50%
- **Question**: "Will it rain in NYC tomorrow?"
- **Good for**: General practice, testing strategies

### 2. Bullish Market 📈
- **Probability**: ~80%
- **Question**: "Will the incumbent win the election?"
- **Good for**: Trading high-probability events

### 3. Bearish Market 📉
- **Probability**: ~20%
- **Question**: "Will the underdog win?"
- **Good for**: Understanding long shots, risk/reward

### 4. Volatile Market 🎢
- **Probability**: Uncertain (~50% but wide spread)
- **Question**: "Will the new tech succeed?"
- **Good for**: Practicing in uncertain conditions

> **Tip**: Start with the Balanced Market to warm up, then try the others.`,
      },
    },

    // Page 3: Trading Tips
    {
      type: 'text',
      data: {
        markdown: `## Trading Tips

Before you dive in, here are some tips:

### Start Small
- Begin with 10-20 share orders
- Get comfortable before trading larger sizes

### Use Both Order Types
- **Market orders** for instant execution
- **Limit orders** for better prices (but you might wait)

### Watch the Spread
- Tight spread = cheaper to trade
- Wide spread = more expensive, be careful

### Track Your Performance
- Check "Your Positions" to see your holdings
- Green = profit, Red = loss
- Your balance shows available cash

### Don't Be Afraid to Lose
- It's virtual money!
- Every loss is a learning opportunity
- Reset and try again if needed

### Think Before You Trade
- Why are you buying/selling?
- What price makes sense?
- How much are you willing to risk?`,
      },
    },

    // Page 4: Link to Sandbox
    {
      type: 'text',
      data: {
        markdown: `## Ready to Practice?

Click the button below to enter the **Sandbox Mode** - a dedicated practice environment with:

- Full simulation controls (play, pause, speed adjustment)
- All trading tools enabled
- Multiple market scenarios to choose from
- Real-time activity feed

<a href="/sandbox" class="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-lg font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
  Enter Sandbox Mode →
</a>

> **Tip**: You can always return to the sandbox from the navigation menu.`,
      },
    },

    // Page 5: Final thoughts
    {
      type: 'text',
      data: {
        markdown: `## You're Ready!

You now understand:
- ✅ What prediction markets are
- ✅ How prices represent probabilities
- ✅ How to read an order book
- ✅ Market orders vs limit orders
- ✅ The bid-ask spread
- ✅ How prices move with supply and demand

### What's Next?

When you're comfortable in the sandbox:
1. **Try real prediction markets** like Polymarket, Kalshi, or Metaculus
2. **Start small** with real money
3. **Focus on markets you understand**
4. **Never bet more than you can afford to lose**

### A Final Word

Prediction markets are powerful tools for understanding the world. They aggregate information from many people into a single, easy-to-understand number.

Whether you trade or just watch, you'll gain insights into how the world sees the future.

**Happy trading!** 🚀`,
      },
    },
  ],
}

// Export all scenarios for use in the app
export const sandboxScenarios = [
  balancedScenario,
  bullishScenario,
  bearishScenario,
  volatileScenario,
]
